import { useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { Documento, StatusDoDocumento } from '@/data/tipos';
import { VisorDeDocumento } from './VisorDeDocumento';

export const STATUS: Record<StatusDoDocumento, { rotulo: string; tom: 'neutral' | 'warning' | 'success' | 'danger' }> = {
    rascunho: { rotulo: 'Rascunho', tom: 'neutral' },
    aguardando: { rotulo: 'Aguardando assinatura', tom: 'warning' },
    assinado: { rotulo: 'Assinado', tom: 'success' },
    rejeitado: { rotulo: 'Recusado', tom: 'danger' },
    cancelado: { rotulo: 'Cancelado', tom: 'danger' },
};

export interface DetalheDoDocumentoProps {
    documento: Documento | null;
    onClose: () => void;
    onDecidido: () => void;
}

export function DetalheDoDocumento({ documento, onClose, onDecidido }: DetalheDoDocumentoProps) {
    const aviso = useAviso();
    const idMotivo = useId();
    const [recusando, setRecusando] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setRecusando(false);
        setMotivo('');
        setErro(null);
    }, [documento]);

    if (!documento) return null;

    const status = STATUS[documento.status];
    const pendente = documento.status === 'aguardando' || documento.status === 'rascunho';

    const decidir = async (novo: StatusDoDocumento) => {
        setSalvando(true);
        try {
            await dados().decidirDocumento(documento.id, novo, novo === 'rejeitado' ? motivo : undefined);
            aviso({
                title: novo === 'assinado' ? 'Assinatura registrada' : novo === 'rejeitado' ? 'Recusa registrada' : 'Documento cancelado',
                description: `${documento.titulo} · ${documento.paciente}`,
            });
            onDecidido();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível registrar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={620}
            title={documento.titulo}
            description={`${documento.paciente} · ${documento.paginas} ${documento.paginas === 1 ? 'página' : 'páginas'}`}
            footer={
                <>
                    {pendente && (
                        <Button variant="ghost" iconLeft={<Icon name="file-x" size={15} />} onClick={() => setRecusando(true)}>
                            Registrar recusa
                        </Button>
                    )}
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onClose}>Fechar</Button>
                    {pendente && (
                        <Button iconLeft={<Icon name="file-check" size={15} />} loading={salvando} onClick={() => void decidir('assinado')}>
                            Registrar assinatura
                        </Button>
                    )}
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {documento.status === 'rejeitado' && documento.motivoDaRecusa && (
                    /* O motivo fica à vista: status vermelho sem porquê não
                       ajuda quem vai falar com a paciente amanhã. */
                    <Alert tone="danger" dismissible={false} title="Documento recusado">
                        {documento.motivoDaRecusa}
                        {documento.recusadoEm && ` · ${dataEHoraPorExtenso(documento.recusadoEm)}`}
                    </Alert>
                )}

                {recusando && (
                    <Alert tone="warning" dismissible={false} title="Por que o documento foi recusado?">
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
                            <span style={{ flex: 1 }}>
                                <FormField label="Motivo" required htmlFor={idMotivo}>
                                    <Input id={idMotivo} value={motivo} onChange={(v) => { setMotivo(v); setErro(null); }} placeholder="Ex.: paciente não autorizou o uso das imagens" />
                                </FormField>
                            </span>
                            <Button onClick={() => void decidir('rejeitado')} loading={salvando} disabled={!motivo.trim()}>Registrar</Button>
                        </div>
                    </Alert>
                )}

                <VisorDeDocumento
                    titulo={documento.titulo}
                    corpo={documento.corpo ?? 'Este documento não tem corpo gravado.'}
                    assinantes={documento.assinantes}
                    etiquetas={[{ texto: status.rotulo, tom: status.tom }]}
                    apoio={[
                        documento.assinantes,
                        documento.enviadoEm && `enviado em ${dataEHoraPorExtenso(documento.enviadoEm)}`,
                        documento.assinadoEm && `assinado em ${dataEHoraPorExtenso(documento.assinadoEm)}`,
                    ].filter(Boolean).join(' · ')}
                />
            </div>
        </Modal>
    );
}
